type FetchOptions = RequestInit & {
  next?: { revalidate?: number | false; tags?: string[] };
  /** Per-call timeout in milliseconds. When set, the request is
   *  aborted via `AbortController` after this many ms and a clear
   *  "timed out" error is thrown. Use for endpoints with known-bad
   *  upstream latency (e.g. supplier-fanout queries) so a slow
   *  partner doesn't hang the user-facing UI. */
  timeoutMs?: number;
};

export type ApiResponse<T> = { message: string; data: T };

/**
 * Server-only request hooks, registered by the app (apps/web `_lib/tenant/register-api-client`).
 *
 * The browser calls same-origin `/api/<svc>/…`, which Next forwards with the signed tenant
 * header. On the server the same client must instead reach the backend directly AND carry the
 * current request's tenant header itself — this package knows nothing about Next, so the app
 * supplies both. Unused in the browser.
 */
export interface ServerRequestHooks {
  /** Map a relative public API URL (`/api/hotels/search`) to the backend URL. */
  resolveUrl(url: string): string;
  /** Extra headers for every server-side call — the current request's tenant. */
  headers(): Promise<Record<string, string>>;
}

// On globalThis, not module scope: Next bundles this package into several server layers, each
// with its own module instance, and the hooks must reach all of them.
const SERVER_HOOKS = Symbol.for('@wl/api-client/server-hooks');

export function setServerRequestHooks(hooks: ServerRequestHooks): void {
  (globalThis as Record<symbol, unknown>)[SERVER_HOOKS] = hooks;
}

function serverHooks(): ServerRequestHooks | undefined {
  if (typeof window !== 'undefined') return undefined;
  return (globalThis as Record<symbol, ServerRequestHooks | undefined>)[SERVER_HOOKS];
}

/**
 * Isomorphic `fetch` for code that calls a service without a client class (it runs in both the
 * browser and SSR). In the browser it is plain `fetch` to the same-origin `/api/<svc>` path; on
 * the server it applies the registered hooks — backend URL plus the request's tenant header.
 */
export async function apiFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const hooks = serverHooks();
  if (!hooks) return fetch(url, init);
  const headers = new Headers(init.headers);
  for (const [name, value] of Object.entries(await hooks.headers())) headers.set(name, value);
  return fetch(hooks.resolveUrl(url), { ...init, headers });
}

/**
 * Error class for non-OK HTTP responses. Carries the HTTP `status` so callers
 * can discriminate 4xx (terminal client error — don't retry) from 5xx /
 * network errors (transient — safe to retry).
 *
 * Network errors (fetch itself rejects — DNS, CORS, offline) propagate as
 * the underlying `TypeError` from `fetch`, which has no `status` — callers
 * can use `err instanceof ApiError` to discriminate.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export class BaseApiClient {
  protected baseUrl: string;

  constructor(baseUrl: string | undefined) {
    // Stored as-is (possibly empty). We deliberately do NOT throw here:
    // every concrete client exports a module-level singleton, and a
    // constructor-time throw would break every consumer that imports
    // the package — even ones that never call this client. Instead the
    // missing-URL check fires lazily inside `request()`, so only the
    // call site that actually needs the env var sees the error.
    this.baseUrl = baseUrl ?? '';
  }

  /** The URL and extra headers for `endpoint` — resolved through the server hooks on the server. */
  protected async target(endpoint: string): Promise<{ url: string; headers: Record<string, string> }> {
    const hooks = serverHooks();
    const url = `${this.baseUrl}${endpoint}`;
    if (!hooks) return { url, headers: {} };
    return { url: hooks.resolveUrl(url), headers: await hooks.headers() };
  }

  protected async request<T>(
    endpoint: string,
    options?: FetchOptions
  ): Promise<T> {
    if (!this.baseUrl) {
      throw new Error(
        `${this.constructor.name}: base URL is not configured. ` +
          'Set the relevant NEXT_PUBLIC_* env var (see apps/web/.env.example).',
      );
    }
    const { url, headers: serverHeaders } = await this.target(endpoint);

    // Optional per-call timeout. We create the AbortController ourselves
    // (rather than relying on AbortSignal.timeout) so we can:
    //   • Throw a recognisable error message ("… timed out after Nms")
    //     instead of a generic AbortError.
    //   • Compose with a caller-provided signal in the future without
    //     reworking the timeout path.
    // The `clearTimeout` in the finally block prevents the timer from
    // firing after a successful response — important for keep-alive
    // pools where late aborts can interrupt the next request.
    const { timeoutMs, signal: callerSignal, ...rest } = options || {};
    let controller: AbortController | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let signal = callerSignal;
    if (timeoutMs && timeoutMs > 0) {
      controller = new AbortController();
      timer = setTimeout(() => controller!.abort(new Error('timeout')), timeoutMs);
      signal = controller.signal;
    }

    let response: Response;
    try {
      response = await fetch(url, {
        ...rest,
        signal,
        headers: {
          'Content-Type': 'application/json',
          ...serverHeaders,
          ...(options?.headers || {}),
        },
      });
    } catch (err: any) {
      if (timer) clearTimeout(timer);
      if (controller?.signal.aborted) {
        throw new Error(
          `${this.constructor.name}: request to ${endpoint} timed out after ${timeoutMs}ms`,
        );
      }
      throw err;
    }
    if (timer) clearTimeout(timer);

    if (!response.ok) {
      // Surface the server's error message (NestJS returns { statusCode, message, error }).
      let message = `HTTP ${response.status} ${response.statusText || 'Request failed'}`;
      let body: unknown = null;
      try {
        body = await response.clone().json();
        if ((body as any)?.message) {
          message = Array.isArray((body as any).message)
            ? (body as any).message.join('; ')
            : (body as any).message;
        } else if (typeof body === 'string' && body) {
          message = body;
        }
      } catch {
        // Non-JSON body (e.g. nginx HTML error page) — try to grab raw text
        try {
          const text = await response.clone().text();
          if (text && text.length < 500) message = `${message}: ${text.trim()}`;
          body = text;
        } catch {
          // ignore
        }
      }
      throw new ApiError(message, response.status, body);
    }

    return response.json() as Promise<T>;
  }

  protected async get<T>(
    endpoint: string,
    params?: Record<string, any>,
    options?: { revalidate?: number; tags?: string[]; timeoutMs?: number; headers?: Record<string, string> }
  ): Promise<T> {
    let url = endpoint;

    // Build query string from params
    if (params && Object.keys(params).length > 0) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
      const queryString = queryParams.toString();
      if (queryString) {
        url = `${endpoint}?${queryString}`;
      }
    }

    const { timeoutMs, headers, ...nextOpts } = options || {};
    return this.request<T>(url, {
      method: 'GET',
      cache: 'no-store',
      next: nextOpts,
      timeoutMs,
      headers,
    });
  }

  protected async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      cache: 'no-store',
    });
  }

  protected async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      cache: 'no-store',
    });
  }

  protected async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
      cache: 'no-store',
    });
  }

  protected async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      cache: 'no-store',
    });
  }
}
